// frontend/src/components/DispatchDialog.js

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Stack,
} from '@mui/material';
import { updateOrderStatusAndTracking } from '../services/api';
import moment from 'moment';

const DispatchDialog = ({ open, onClose, order, onDispatched }) => {
    const [trackingNumber, setTrackingNumber] = useState('');
    // Initialize dispatchedDate with current date and time in 'YYYY-MM-DDTHH:mm' format
    const [dispatchedDate, setDispatchedDate] = useState(moment().format('YYYY-MM-DDTHH:mm'));
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleDispatch = async () => {
        setIsSubmitting(true);
        setError('');
        try {
            // Call the API and get the response
            const result = await updateOrderStatusAndTracking(order.orderId, 'Dispatched', trackingNumber, dispatchedDate, order.orderNumber);

            // Check result.success (the backend should always return this)
            if (result && result.success) {
                if (onDispatched) onDispatched(); // Only call if provided
                onClose();
            } else {
                setError(result?.message || 'Error dispatching order.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error dispatching order.');
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleDateChange = (e) => {
        const selectedDateTime = e.target.value;
        const now = moment();
        const selectedMoment = moment(selectedDateTime, 'YYYY-MM-DDTHH:mm', true);

        if (!selectedMoment.isValid()) {
            setError('Invalid date and time format.');
            return;
        }

        if (selectedMoment.isAfter(now)) {
            setError('Dispatched date and time cannot be in the future.');
        } else {
            setError('');
            setDispatchedDate(selectedDateTime);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Confirm Dispatch</DialogTitle>
            <DialogContent>
                <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <BoxText label="Order" value={order.external_ref || order.orderNumber} />
                    <Typography variant="body2" color="text.secondary">
                        Record dispatch details for this order. Add tracking if it is available now.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Tracking Number (optional)"
                        placeholder="Enter tracking reference"
                        type="text"
                        fullWidth
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Dispatched Date & Time"
                        type="datetime-local"
                        fullWidth
                        value={dispatchedDate}
                        onChange={handleDateChange}
                        helperText="Use the actual handoff time. Future times are not allowed."
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            max: moment().format('YYYY-MM-DDTHH:mm'), // Prevent future dates and times
                        }}
                    />
                    {error && <Typography color="error" variant="body2">{error}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Close</Button>
                <Button
                    onClick={handleDispatch}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !!error}
                >
                    {isSubmitting ? 'Saving Dispatch...' : 'Confirm Dispatch'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const BoxText = ({ label, value }) => (
    <Stack direction="row" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {label}:
        </Typography>
        <Typography variant="body2" color="text.secondary">
            {value}
        </Typography>
    </Stack>
);

export default DispatchDialog;
